import { HTMLAttributes, ReactNode, ThHTMLAttributes, TdHTMLAttributes } from "react";
import clsx from "clsx";
import { ChevronUpIcon, ChevronDownIcon } from "@heroicons/react/24/outline";

// Table container
export interface TableProps extends HTMLAttributes<HTMLTableElement> {
  responsive?: boolean;
}

export const Table = ({ responsive = true, className, children, ...props }: TableProps) => {
  const table = (
    <table className={clsx("min-w-full divide-y", className)} style={{ borderColor: 'var(--color-border)' }} {...props}>
      {children}
    </table>
  );

  if (responsive) {
    return <div className="overflow-x-auto">{table}</div>;
  }

  return table;
};

// Table head
export interface TableHeadProps extends HTMLAttributes<HTMLTableSectionElement> {}

export const TableHead = ({ className, children, ...props }: TableHeadProps) => {
  return (
    <thead className={clsx("", className)} style={{ backgroundColor: 'var(--color-bg-sunken)' }} {...props}>
      {children}
    </thead>
  );
};

// Table body
export interface TableBodyProps extends HTMLAttributes<HTMLTableSectionElement> {}

export const TableBody = ({ className, children, ...props }: TableBodyProps) => {
  return (
    <tbody className={clsx("divide-y", className)} style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }} {...props}>
      {children}
    </tbody>
  );
};

// Table row
export interface TableRowProps extends HTMLAttributes<HTMLTableRowElement> {
  clickable?: boolean;
}

export const TableRow = ({ clickable = false, className, children, ...props }: TableRowProps) => {
  return (
    <tr className={clsx(clickable && "hover:bg-surface-sunken cursor-pointer transition-colors", className)} {...props}>
      {children}
    </tr>
  );
};

// Table header cell
export interface TableHeaderProps extends ThHTMLAttributes<HTMLTableCellElement> {
  sortable?: boolean;
  sortDirection?: "asc" | "desc" | null;
  onSort?: () => void;
}

export const TableHeader = ({ sortable = false, sortDirection = null, onSort, className, children, ...props }: TableHeaderProps) => {
  return (
    <th
      scope="col"
      className={clsx(
        "px-6 py-3 text-left text-heading-xs uppercase tracking-wider",
        sortable && "cursor-pointer select-none hover:bg-surface-sunken",
        className
      )}
      onClick={sortable ? onSort : undefined}
      {...props}
    >
      <div className="flex items-center gap-2">
        {children}
        {sortable && (
          <span className="flex flex-col">
            <ChevronUpIcon className={clsx("h-3 w-3", sortDirection === "asc" ? "text-primary-500" : "text-navy-muted")} />
            <ChevronDownIcon className={clsx("h-3 w-3 -mt-1", sortDirection === "desc" ? "text-primary-500" : "text-navy-muted")} />
          </span>
        )}
      </div>
    </th>
  );
};

// Table cell
export interface TableCellProps extends TdHTMLAttributes<HTMLTableCellElement> {}

export const TableCell = ({ className, children, ...props }: TableCellProps) => {
  return (
    <td className={clsx("px-6 py-4 whitespace-nowrap text-sm", className)} style={{ color: 'var(--color-text-primary)' }} {...props}>
      {children}
    </td>
  );
};

// Empty state
export interface TableEmptyProps {
  message?: string;
  icon?: ReactNode;
  action?: ReactNode;
  colSpan?: number;
}

export const TableEmpty = ({ message = "No data available", icon, action, colSpan }: TableEmptyProps) => {
  return (
    <tr>
      <td colSpan={colSpan} className="px-6 py-12 text-center">
        <div className="flex flex-col items-center justify-center">
          {icon && <div className="mb-3" style={{ color: 'var(--color-text-muted)' }}>{icon}</div>}
          <p className="text-sm mb-4" style={{ color: 'var(--color-text-secondary)' }}>{message}</p>
          {action && <div>{action}</div>}
        </div>
      </td>
    </tr>
  );
};

// Pagination
export interface TablePaginationProps {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  pageSize: number;
  onPageChange: (page: number) => void;
  onPageSizeChange?: (pageSize: number) => void;
}

export const TablePagination = ({ currentPage, totalPages, totalItems, pageSize, onPageChange, onPageSizeChange }: TablePaginationProps) => {
  const startItem = (currentPage - 1) * pageSize + 1;
  const endItem = Math.min(currentPage * pageSize, totalItems);

  return (
    <div className="flex items-center justify-between px-6 py-3 border-t" style={{ backgroundColor: 'var(--color-bg-raised)', borderColor: 'var(--color-border)' }}>
      <div className="flex items-center gap-2">
        <span className="text-sm" style={{ color: 'var(--color-text-secondary)' }}>
          Showing <span className="font-medium">{startItem}</span> to <span className="font-medium">{endItem}</span> of{" "}
          <span className="font-medium">{totalItems}</span> results
        </span>
        {onPageSizeChange && (
          <select
            value={pageSize}
            onChange={(e) => onPageSizeChange(Number(e.target.value))}
            className="ml-2 select text-sm"
          >
            <option value={10}>10</option>
            <option value={25}>25</option>
            <option value={50}>50</option>
            <option value={100}>100</option>
          </select>
        )}
      </div>

      <div className="flex gap-2">
        <button
          onClick={() => onPageChange(currentPage - 1)}
          disabled={currentPage === 1}
          className="px-3 py-1 text-sm border rounded hover:bg-surface-sunken disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)' }}
        >
          Previous
        </button>
        <button
          onClick={() => onPageChange(currentPage + 1)}
          disabled={currentPage === totalPages}
          className="px-3 py-1 text-sm border rounded hover:bg-surface-sunken disabled:opacity-50 disabled:cursor-not-allowed"
          style={{ color: 'var(--color-text-primary)', borderColor: 'var(--color-border)' }}
        >
          Next
        </button>
      </div>
    </div>
  );
};
